import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LockerProcessing.css"; 

const LockerProcessing = () => {
  const { t } = useTranslation("lockerprocessing");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const mainCode = location.state?.mainCode || params.get("mainCode") || sessionStorage.getItem("mainCode");
  const lockerNo = location.state?.lockerNo || params.get("lockerNo") || sessionStorage.getItem("lockerNo");
  const type = location.state?.type || params.get("type") || sessionStorage.getItem("locker_return_type");
  const startTime = location.state?.startTime ?? params.get("startTime") ?? sessionStorage.getItem("locker_return_startTime") ?? localStorage.getItem("locker_return_startTime");
  const endTime = location.state?.endTime ?? params.get("endTime") ?? sessionStorage.getItem("locker_return_endTime") ?? localStorage.getItem("locker_return_endTime");
  const logId = location.state?.logId || params.get("logId") || sessionStorage.getItem("locker_return_logId");
  const cost = location.state?.cost || params.get("cost") || sessionStorage.getItem("locker_return_cost");
  const password = location.state?.password;
  const retry = location.state?.retry;
  const resultCode = location.state?.resultCode || params.get("resultCode");
  const cardCode = location.state?.cardCode || params.get("cardCode");

  const [loading, setLoading] = useState(true);
  const unitcode = sessionStorage.getItem("UNITCODE");

  //console.log(`Page LockerProcessing : ${mainCode} ${unitcode} ${lockerNo} ${type} ${startTime} ${endTime} ${logId} ${cost}`);

  try {
    sessionStorage.removeItem("locker_return_startTime");
    localStorage.removeItem("locker_return_startTime");
  } catch (e) {
    console.warn("Could not remove locker_return_startTime from storage:", e);
  }

useEffect(() => {
  let warned = false;

  // 히스토리 무한히 쌓아서 뒤로가기 무력화
  const pushHistory = () => {
    window.history.pushState(null, '', window.location.href);
  };

  const handlePopState = () => {
    if (!warned) {
      //alert('이 페이지에서는 뒤로가기를 사용할 수 없습니다.');
      warned = true;
    }
    pushHistory(); // 다시 push해서 절대 뒤로 못 가게 함
  };

  const handleBeforeUnload = (e) => {
    e.preventDefault();
    e.returnValue = ''; // 새로고침/닫기 시 경고창 띄움
  };

  const handleKeyDown = (e) => {
    const key = e.key.toLowerCase();
    if (
      key === 'f5' ||
      (e.ctrlKey && key === 'r') ||
      (e.metaKey && key === 'r')
    ) {
      e.preventDefault();
    }
  };

  // 최초 히스토리 push + 반복 쌓기 (브라우저 속이기)
  pushHistory();
  const interval = setInterval(pushHistory, 300); // 너무 빠르면 브라우저 막힘

  window.addEventListener('popstate', handlePopState);
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('keydown', handleKeyDown);

  return () => {
    clearInterval(interval);
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('keydown', handleKeyDown);
  };
}, []);


  useEffect(() => {
    if (!lockerNo || !mainCode) {
      alert(t("invalid_access"));
      navigate("/"); 
      return;
    }

    // 2025년 이전 날짜 검증
    const validateDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return false;
      try {
        const date = new Date(dateTimeStr);
        return date.getFullYear() >= 2025;
      } catch (error) {
        return false;
      }
    };

    if (!validateDateTime(startTime) || !validateDateTime(endTime)) {
      alert(t("fail_payment"));
      navigate("/");
      return;
    }

	  if (retry) {
      // 재시도일 경우 바로 문 열기만 시도
      callDoorOpenApi(unitcode, lockerNo);
      return;
	  }

    if (mainCode === "F") {
      finalizeFindUse();
    } else if (mainCode === "R") {
      finalizeReservedUse();
    } else if (mainCode === "O") {
      validateEndDateBeforeOpen();
    } else {
      console.log("보관함 열기 API 호출 시작...");
      callDoorOpenApi(unitcode, lockerNo);
    }

  }, [unitcode, lockerNo, mainCode, retry, navigate]); 

  const generateReservedUserId = () => {
    const now = new Date();
    const unitcode = sessionStorage.getItem("UNITCODE") || "0000000000";
    const siteCode = unitcode.substring(5, 9); // 6~9번째 자리
    const y = String(now.getFullYear()).slice(2);
    const M = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    return `C${siteCode}${y}${M}${d}${h}${m}${s}`; // C + siteCode + YYMMDDHHMMSS
  };

  const finalizeFindUse = async () => {
    try {
      const res = await fetch("/api/lockerfinalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unitcode, lockerNo: parseInt(lockerNo), mainCode: "F", logId, cost, resultCode, cardCode })
      });
      const result = await res.json();
      if (result.success) {
        callDoorOpenApi(unitcode, lockerNo);
      } else {
        alert(result.message || t("door_open_failed"));
        navigate("/");
      }
    } catch (err) {
      console.error("finalizeFindUse API 오류:", err);
      alert(t("door_open_check_error"));
      navigate("/");
    }
  };

  const finalizeReservedUse = async () => {
    const reservedUserId = generateReservedUserId();
    sessionStorage.setItem("reservedUserId", reservedUserId);
    const userName = (type === "주간결제" || type === "장기결제") ? "현장등록" : "";
    const lockerType = (type === "주간결제" || type === "장기결제") ? "P" : "N";
    const locker_exclusive_use = (type === "주간결제" || type === "장기결제") ? "128" : "0";
    const user_pwd = sessionStorage.getItem("password");
    const user_mobile_no = sessionStorage.getItem("phoneNumber");

    try {
      const res = await fetch("/api/lockerfinalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unitcode,
          lockerNo: parseInt(lockerNo),
          mainCode: "R",
          startTime,
          endTime,
          logId,
          type,
          reservedUserId,
          userName,
          lockerType,
          user_pwd,
          user_mobile_no,
          locker_exclusive_use,
          cost,
          resultCode,
          cardCode
        })
      });
      const result = await res.json();
      if (result.success) {
        callDoorOpenApi(unitcode, lockerNo);
      } else {
        alert(result.message || t("door_open_failed"));
        navigate("/");
      }
    } catch (err) {
      console.error("finalizeReservedUse API 오류:", err);
      alert(t("door_open_check_error"));
      navigate("/");
    }
  };

  const validateEndDateBeforeOpen = async () => {
    try {
      const res = await fetch("/api/lockerfinalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unitcode, lockerNo: parseInt(lockerNo), mainCode: "O", user_pwd: password })
      });
      const result = await res.json();
      if (result.success) {
        callDoorOpenApi(unitcode, lockerNo);
      } else {
        alert(result.message || t("locker_expired"));
        navigate("/");
      }
    } catch (err) {
      console.error("validateEndDate API 오류:", err);
      alert(t("door_open_check_error"));
      navigate("/");
    }
  };

  const callDoorOpenApi = async (unitcode, lockerNo) => {
    setLoading(true); 

    const minimumDisplayTime = 2000; // 최소 표시 시간 (밀리초)
    await new Promise(resolve => setTimeout(resolve, minimumDisplayTime));

    console.log(`보관함 열기 API 호출 시도: UNITCODE=${unitcode}, lockerNo=${lockerNo}`);

    let apiSuccess = false;
    let apiMessage = '';
    let dbErrorMessage = '';

    try {
      const response = await fetch('/api/dooropen', { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
		body: JSON.stringify({ unitcode, locker_no: parseInt(lockerNo, 10)}),
      });

      const result = await response.json();
      console.log('Door Open API Response:', result);

      apiSuccess = result.success;
      apiMessage = result.message;
      dbErrorMessage = result.dbErrorMessage; 

    } catch (error) {
      console.error('Error calling door open API:', error);
      apiSuccess = false; 
      alert(t("door_open_comm_error"));
    } finally {
        setLoading(false); 

        const finalResult = {
          mainCode: mainCode,
          lockerNo: lockerNo,     
          lockerOpened: apiSuccess,
          apiMessage: apiMessage, 
          dbErrorMessage: dbErrorMessage,
          startTime, 
          endTime
        };

        sessionStorage.setItem("LockerCompleteData", JSON.stringify(finalResult));

        console.log("/LockerComplete로 결과와 함께 이동:", finalResult);
        navigate("/LockerComplete", { state: finalResult });
    }
  };

  return (
    <div className="processing-main">
      <div className="processing-box">
        {loading ? (
          <>
            <div className="spinner" />
            <h2>{t("opening_locker")}</h2>
            <p>{t("please_wait")}</p>
          </>
        ) : (
          null
        )}
      </div>
    </div>
  );
};

export default LockerProcessing;